import React, { useState, useEffect } from "react";
import moment from "moment";
import { FaCheckCircle, FaTimesCircle, FaPlus, FaMinus } from "react-icons/fa";
import {
  hasUsableDynamicPayload,
  parseChecklistPayload,
} from "../../utils/dynamicChecklistExport";
import DynamicChecklistPreview from "./DynamicChecklistPreview";

interface ChecklistData {
  title: string;
  date: string;
  officerType: string;
  downloadedBy: string;
  Current_json: string;
}

const PreviewChecklist: React.FC<{ data: any }> = ({ data }) => {
  const parsedFromPayload = parseChecklistPayload(data?.Current_json);
  if (hasUsableDynamicPayload(parsedFromPayload)) {
    return <DynamicChecklistPreview data={data} />;
  }

  let parsed: any = parsedFromPayload;

  if (!parsed && data?.Current_json) {
    try {
      parsed = JSON.parse(data.Current_json);
    } catch (e) {
      console.error("Invalid JSON in Current_json", e);
      parsed = null;
    }
  }

  // Meaningful value checks
  const isMeaningfulValue = (value: any): boolean => {
    if (value === null || value === undefined) return false;
    if (typeof value === "boolean") return value === true;
    if (typeof value === "string") return value.trim() !== "";
    if (typeof value === "number") return true;
    if (Array.isArray(value)) return value.some(isMeaningfulValue);
    if (typeof value === "object") {
      // Check for empty object
      if (Object.keys(value).length === 0) return false;
      // Special handling for objects with "value" property
      if ("value" in value) {
        return isMeaningfulValue(value.value) || value.verified === true;
      }
      return Object.values(value).some(isMeaningfulValue);
    }
    return false;
  };

  const isMeaningfulRow = (row: any): boolean => {
    if (!row || typeof row !== "object") return false;

    // Check for any meaningful value in the row
    for (const key in row) {
      if (isMeaningfulValue(row[key])) {
        return true;
      }
    }
    return false;
  };

  const hasMeaningfulRows = (rows: any[]): boolean => {
    if (!Array.isArray(rows)) return false;
    return rows.some(isMeaningfulRow);
  };

  const cleanRows = (rows: any[]) => rows?.filter(isMeaningfulRow) || [];

  // Helper function to check if a specific subsection should be shown
  const shouldShowSubSection = (
    section: any,
    showStatusKey: string,
  ): boolean => {
    if (section[showStatusKey] === undefined) return true; // No show status, default to show if has data
    return section[showStatusKey] === true || section[showStatusKey] === "true";
  };

  // Check if entire section has meaningful data
  const isSectionMeaningful = (key: string): boolean => {
    if (!parsed || !parsed[key]) return false;

    const section = parsed[key];

    // Function to check if section has meaningful data
    const hasMeaningfulData = (): boolean => {
      switch (key) {
        case "generalVerification":
          // Check if section has a show status
          if (!shouldShowSubSection(section, "isshowgeneralVerificationStatus"))
            return false;

          return (
            isMeaningfulValue(section.isSpellingCorrect) ||
            isMeaningfulValue(section.ownerShip) ||
            (section.file &&
              Object.values(section.file).some(isMeaningfulValue))
          );

        case "surveyNoVerification":
          // Check each subsection independently
          const shouldShowRegister = shouldShowSubSection(
            section,
            "isshowregisterStatus",
          );
          const shouldShowCurrentPatta = shouldShowSubSection(
            section,
            "isshowcurrentPattaStatus",
          );
          const shouldShowTitleDeed = shouldShowSubSection(
            section,
            "isshowtitleDeedStatus",
          );

          // Only check data for subsections that should be shown
          const hasRegisterData = shouldShowRegister
            ? hasMeaningfulRows(section.register || [])
            : false;
          const hasCurrentPattaData = shouldShowCurrentPatta
            ? hasMeaningfulRows(section.currentPatta || [])
            : false;
          const hasTitleDeedData = shouldShowTitleDeed
            ? hasMeaningfulRows(section.titleDeed || [])
            : false;

          // Check other fields (these should always show if they have data, as they don't have individual show status)
          const hasOtherData =
            isMeaningfulValue(section.totalTitleDeed) ||
            isMeaningfulValue(section.totalEc) ||
            isMeaningfulValue(section.difference) ||
            isMeaningfulValue(section.missingEc) ||
            section.type === true ||
            section.conditionPatta === true;

          return (
            hasRegisterData ||
            hasCurrentPattaData ||
            hasTitleDeedData ||
            hasOtherData
          );

        case "deed":
          // Check each deed type's show status
          const deedTypes = [
            { key: "saleDeed", showStatus: "isshowsaleDeedStatus" },
            { key: "giftDeed", showStatus: "isshowgiftDeedStatus" },
            { key: "settlementDeed", showStatus: "isshowsettlementDeedStatus" },
            { key: "partitionDeed", showStatus: "isshowpartitionDeedStatus" },
            { key: "willDeed", showStatus: "isshowwillDeedStatus" },
            { key: "leaseDeed", showStatus: "isshowleaseDeedStatus" },
            { key: "attorneyDeed", showStatus: "isshowattorneyDeedStatus" },
            { key: "releaseDeed", showStatus: "isshowreleaseDeedStatus" },
            {
              key: "rectificationDeed",
              showStatus: "isshowrectificationDeedStatus",
            },
            { key: "consentDeed", showStatus: "isshowconsentDeedStatus" },
            {
              key: "missingDocument",
              showStatus: "isshowmissingDocumentStatus",
            },
          ];

          let hasAnyDeedData = false;

          // Check each deed type if its show status is true
          for (const deedType of deedTypes) {
            if (shouldShowSubSection(section, deedType.showStatus)) {
              if (hasMeaningfulRows(section[deedType.key] || [])) {
                hasAnyDeedData = true;
                break;
              }
            }
          }

          // Check other deed-related fields
          const hasPlotRegularization =
            shouldShowSubSection(section, "ishowplotregulationstatus") &&
            section.plotRegularization &&
            (section.plotRegularization.isDeedRegisteredAfter30_9_2016 ===
              true ||
              section.plotRegularization.isPlotObtained === true);

          const hasGazetta =
            shouldShowSubSection(section, "ishowGazettestatus") &&
            section.gazetteofDoc &&
            (section.gazetteofDoc.isNameChanged === true ||
              section.gazetteofDoc.gazettePublication === true ||
              section.gazetteofDoc.NameChangedbythasildar === true ||
              section.gazetteofDoc.IndementyBond === true ||
              section.gazetteofDoc.docList !== "");

          const hasForeignPower =
            shouldShowSubSection(section, "ishowAdjudicationstatus") &&
            section.foriegnPower &&
            (section.foriegnPower.isForiegnPowerRegistered === true ||
              isMeaningfulValue(section.foriegnPower.docNo));

          const hasMortgageInPowerDoc = section.mortgageInPowerDoc === true;

          return (
            hasAnyDeedData ||
            hasPlotRegularization ||
            hasForeignPower ||
            hasGazetta ||
            hasMortgageInPowerDoc
          );

        case "HsdpattasVerify":
          // Only show if show status is true
          if (!shouldShowSubSection(section, "isshowHsdpattasStatus"))
            return false;
          return hasMeaningfulRows(section.Hsdpattas || []);

        case "nathamPattaverify":
          if (!shouldShowSubSection(section, "isshownathamPattaStatus"))
            return false;
          return hasMeaningfulRows(section.nathamPatta || []);

        case "legalHeirsVerify":
          if (!shouldShowSubSection(section, "isshowlegalHeirsStatus"))
            return false;
          return hasMeaningfulRows(section.legalHeirs || []);

        case "ecVerify":
          // ecVerify doesn't have show status in your data, so always check data
          return hasMeaningfulRows(section.ecentry || []);

        case "flow":
          if (!shouldShowSubSection(section, "isshowflowStatus")) return false;
          return hasMeaningfulRows(section.flow || []);

        case "propertytax":
          if (!shouldShowSubSection(section, "isshowpropertytaxStatus"))
            return false;
          return hasMeaningfulRows(section.propertytax || []);

        case "EBreceiptData":
          if (!shouldShowSubSection(section, "isshowEBreceiptStatus"))
            return false;
          return hasMeaningfulRows(section.EBreceipt || []);

        case "missingLOD":
          // missingLOD doesn't have show status in your data, so always check data
          return hasMeaningfulRows(section.sheets || []);

        case "verification":
          // Check each verification type
          const verificationTypes = [
            { key: "company", showStatus: "isshowcompanyStatus" },
            { key: "trust", showStatus: "isshowtrustStatus" },
            { key: "partnership", showStatus: "isshowpartnershipStatus" },
            { key: "association", showStatus: "isshowassociationStatus" },
            { key: "society", showStatus: "isshowsocietyStatus" },
            { key: "appartment", showStatus: "isshowappartmentStatus" },
          ];

          for (const type of verificationTypes) {
            if (shouldShowSubSection(section, type.showStatus)) {
              const subSection = section[type.key];
              if (
                subSection &&
                Object.values(subSection).some(isMeaningfulValue)
              ) {
                return true;
              }
            }
          }
          return false;

        case "courtDocVerification":
          // Check each court doc type
          const courtDocTypes = [
            { key: "partitionSuit", showStatus: "isshowPartitionSuitStatus" },
            {
              key: "declarationSuit",
              showStatus: "isshowDeclarationSuitStatus",
            },
            { key: "lokAdalat", showStatus: "isshowLokAdalatStatus" },
            { key: "specificSuit", showStatus: "isshowSpecificSuitStatus" },
            { key: "appeal", showStatus: "isshowAppealStatus" },
            {
              key: "courtAuctionPurchase",
              showStatus: "isshowCourtAuctionPurchaseStatus",
            },
            {
              key: "sarfesiProceeding",
              showStatus: "isshowSarfesiProceedingStatus",
            },
          ];

          // Also check the main court doc status
          if (shouldShowSubSection(section, "isshowCourtDocStatus")) {
            for (const type of courtDocTypes) {
              const subSection = section[type.key];
              if (
                subSection &&
                Object.values(subSection).some(isMeaningfulValue)
              ) {
                return true;
              }
            }
          }

          // Check individual court doc types
          for (const type of courtDocTypes) {
            if (shouldShowSubSection(section, type.showStatus)) {
              const subSection = section[type.key];
              if (
                subSection &&
                Object.values(subSection).some(isMeaningfulValue)
              ) {
                return true;
              }
            }
          }
          return false;

        case "originalDocVerification":
        case "remarks":
          // These don't have show status fields, so always check for data
          return Object.values(section).some(isMeaningfulValue);

        default:
          return Object.values(section).some(isMeaningfulValue);
      }
    };

    return hasMeaningfulData();
  };

  // Only initialize meaningful sections as open
  const meaningfulSections = parsed
    ? Object.keys(parsed).filter(isSectionMeaningful)
    : [];

  const [openSections, setOpenSections] = useState<Set<string>>(
    new Set(meaningfulSections),
  );

  const toggleSection = (section: string) => {
    setOpenSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(section)) newSet.delete(section);
      else newSet.add(section);
      return newSet;
    });
  };

  const isOpen = (section: string) => openSections.has(section);

  const getBgColor = (bgColor: string) => {
    if (bgColor === "lightgreen")
      return { bg: "#d4edda", text: "#155724", border: "#c8e6c9" };
    if (bgColor === "#ffff8d")
      return { bg: "#fff3cd", text: "#856404", border: "#ffeeba" };
    return { bg: "#007bff", text: "#fff", border: "#007bff" };
  };

  const SectionHeader: React.FC<{
    title: string;
    sectionKey: string;
    bgColor?: string;
  }> = ({ title, sectionKey, bgColor }) => {
    const colors = getBgColor(bgColor || "#007bff");
    return (
      <div
        onClick={() => toggleSection(sectionKey)}
        style={{
          padding: "14px 20px",
          backgroundColor: colors.bg,
          color: colors.text,
          fontWeight: 600,
          fontSize: "16px",
          borderRadius: "8px 8px 0 0",
          cursor: "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          border: `1px solid ${colors.border}`,
          userSelect: "none",
        }}
      >
        <span>{title}</span>
        {isOpen(sectionKey) ? <FaMinus /> : <FaPlus />}
      </div>
    );
  };

  const Bool: React.FC<{ value: boolean }> = ({ value }) => (
    <span
      style={{
        display: "flex",
        alignItems: "center",
        gap: "6px",
        fontWeight: 500,
      }}
    >
      {value ? (
        <FaCheckCircle color="#28a745" />
      ) : (
        <FaTimesCircle color="#dc3545" />
      )}
      {value ? "Yes" : "No"}
    </span>
  );

const Field: React.FC<{
  label: string;
  value: any;
  showVerified?: boolean;
}> = ({ label, value, showVerified = false }) => {
  // Debug logging
  useEffect(() => {
    if (label.includes("Address") || label.includes("address")) {
      console.log(`Field debug - ${label}:`, JSON.stringify(value, null, 2));
    }
  }, [label, value]);

  // Helper function to check if an object is empty
  const isEmptyObject = (obj: any): boolean => {
    return typeof obj === 'object' && obj !== null && Object.keys(obj).length === 0;
  };

  // Helper function to check if value has specific property
  const hasValueProperty = (obj: any): obj is { value: any } => {
    return typeof obj === 'object' && obj !== null && 'value' in obj;
  };

  // Helper function to check if value has verified property
  const hasVerifiedProperty = (obj: any): obj is { verified: boolean; value?: any } => {
    return typeof obj === 'object' && obj !== null && 'verified' in obj;
  };

  // Helper function to extract meaningful value from an object
  const extractDisplayValue = (obj: any): string => {
    if (obj == null) return "—";
    
    // If it's a string or number, return it
    if (typeof obj === 'string' || typeof obj === 'number' || typeof obj === 'boolean') {
      return String(obj);
    }
    
    // If it's an array, join the extracted values
    if (Array.isArray(obj)) {
      const extracted = obj.map(extractDisplayValue).filter(val => val !== "—");
      return extracted.length > 0 ? extracted.join(", ") : "—";
    }
    
    // If it's an object with a value property, extract from that
    if (hasValueProperty(obj) && obj.value != null) {
      return extractDisplayValue(obj.value);
    }
    
    // If it's an object with other properties, try to find a meaningful value
    if (typeof obj === 'object' && obj !== null) {
      // Skip empty objects
      if (isEmptyObject(obj)) return "—";
      
      // Try to find string/number properties
      const meaningfulProps = Object.entries(obj)
        .filter(([key, val]) => {
          if (val == null) return false;
          if (typeof val === 'string' && val.trim() === '') return false;
          if (typeof val === 'object' && isEmptyObject(val)) return false;
          return true;
        })
        .map(([key, val]) => extractDisplayValue(val));
      
      if (meaningfulProps.length > 0) {
        return meaningfulProps.join(", ");
      }
      
      return "—";
    }
    
    // Fallback
    return "—";
  };

  const renderContent = () => {
    // 1. Handle null/undefined
    if (value == null) {
      return "—";
    }
    
    // 2. Handle boolean
    if (typeof value === "boolean") {
      return <Bool value={value} />;
    }
    
    // 3. Handle string or number
    if (typeof value === "string" || typeof value === "number") {
      return String(value);
    }
    
    // 4. Handle array
    if (Array.isArray(value)) {
      if (value.length === 0) return "—";
      
      // Use extractDisplayValue which handles objects properly
      const displayValue = extractDisplayValue(value);
      return displayValue;
    }
    
    // 5. Handle object
    if (typeof value === "object" && value !== null) {
      // Check for empty object
      if (isEmptyObject(value)) {
        return "—";
      }
      
      // SPECIAL CASE: Object with verified and value properties
      const hasVerified = hasVerifiedProperty(value);
      const hasValue = hasValueProperty(value);
      
      if (hasVerified || hasValue) {
        const displayValue = hasValueProperty(value) ? value.value : undefined;
        const isVerified = hasVerifiedProperty(value) ? value.verified === true : false;
        
        // Extract the display value using our helper
        const extractedValue = displayValue != null ? extractDisplayValue(displayValue) : "—";
        
        // Render with verified badge if showVerified is true
        if (showVerified) {
          return (
            <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
              <span>{extractedValue}</span>
              <span
                style={{
                  fontSize: "12px",
                  padding: "2px 8px",
                  backgroundColor: isVerified ? "#d4edda" : "#f8d7da",
                  color: isVerified ? "#155724" : "#721c24",
                  borderRadius: "4px",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                }}
              >
                {isVerified ? "Verified" : "Not Verified"}
              </span>
            </div>
          );
        }
        
        // Render without verified badge
        return extractedValue;
      }
      
      // For any other object, try to extract meaningful values
      return extractDisplayValue(value);
    }
    
    // 6. Fallback
    try {
      return String(value);
    } catch {
      return "—";
    }
  };

  return (
    <div
      style={{
        marginBottom: "12px",
        display: "flex",
        flexDirection: "column",
        gap: "10px",
      }}
    >
      <span style={{ fontWeight: 600, minWidth: "280px", color: "#495057" }}>
        {label}
      </span>
      <div style={{ fontWeight: 500 }}>{renderContent()}</div>
    </div>
  );
};

  const Table: React.FC<{
    data: any[];
    columns: { key: string; label: string }[];
    title?: string;
  }> = ({ data, columns, title }) => {
    const filtered = cleanRows(data);
    if (filtered.length === 0) return null;

    return (
      <div style={{ marginBottom: "20px", overflowX: "scroll" }}>
        {title && <strong>{title}</strong>}
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            margin: "10px 0",
            overflowX: "scroll",
          }}
        >
          <thead>
            <tr style={{ backgroundColor: "#f8f9fa" }}>
              {columns.map((col, index) => (
                <th
                  key={`header-${col.key}-${index}`}
                  style={{
                    padding: "10px",
                    border: "1px solid #dee2e6",
                    textAlign: "left" as const,
                    fontWeight: 600,
                  }}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((row, rowIndex) => (
              <tr key={`row-${rowIndex}`}>
                {columns.map((col, colIndex) => {
                  const value = row?.[col.key];
                  
                  // Safe rendering for table cells
                  const renderTableCell = () => {
                    try {
                      // Handle null/undefined
                      if (value === null || value === undefined) {
                        return "—";
                      }
                      
                      // Handle boolean
                      if (typeof value === "boolean") {
                        return <Bool value={value} />;
                      }
                      
                      // Handle empty object
                      if (typeof value === "object" && value !== null) {
                        if (Object.keys(value).length === 0) {
                          return "—";
                        }
                        
                        // If object has a 'value' property, use that
                        if ("value" in value && value.value !== null && value.value !== undefined) {
                          return String(value.value);
                        }
                        
                        // Try to get first property value
                        const firstKey = Object.keys(value)[0];
                        const firstValue = value[firstKey];
                        if (firstValue !== null && firstValue !== undefined) {
                          return String(firstValue);
                        }
                        
                        return "Object";
                      }
                      
                      // Handle everything else
                      return String(value);
                    } catch (error) {
                      console.error(`Error in table cell [${rowIndex}, ${col.key}]:`, error, value);
                      return "Error";
                    }
                  };
                  
                  return (
                    <td
                      key={`${rowIndex}-${colIndex}-${col.key}`}
                      style={{
                        padding: "10px",
                        border: "1px solid #dee2e6",
                        textAlign: "center",
                      }}
                    >
                      {renderTableCell()}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const SubSection: React.FC<{ title: string; children: React.ReactNode }> = ({
    title,
    children,
  }) => (
    <div style={{ marginTop: "20px" }}>
      <h4 style={{ fontSize: "15px", fontWeight: 600, marginBottom: "10px" }}>
        {title}
      </h4>
      {children}
    </div>
  );

  const sectionStyle = (bgColor: string) => ({
    padding: "20px",
    backgroundColor: "#ffffff",
    border: `1px solid ${getBgColor(bgColor).border}`,
    borderTop: "none",
    borderRadius: "0 0 8px 8px",
  });

  if (!parsed) {
    return <div>Invalid or missing data</div>;
  }

  return (
    <div
      style={{
        height: "70vh",
        margin: "auto",
        fontFamily: '"Segoe UI", Roboto, sans-serif',
        backgroundColor: "#f8f9fa",
        borderRadius: "12px",
        overflow: "scroll",
        overflowX: "hidden",
        boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
      }}
    >
      <div
        style={{
          backgroundColor: "#70767bff",
          color: "white",
          padding: "20px",
          textAlign: "center",
        }}
      >
        <h2 style={{ margin: 0, fontSize: "24px", fontWeight: 600 }}>
          {data.title} - Checklist Preview
        </h2>
        <div style={{ marginTop: "10px", fontSize: "14px", opacity: 0.9 }}>
          {data.file_id} • Login at{" "}
          {moment(data.login_dt).format("DD MMMM YYYY, hh:mm A")} •{" "}
          {data.bank_name}
        </div>
        <div style={{ marginTop: "10px", fontSize: "14px", opacity: 0.9 }}>
          Process Checklist Completed at{" "}
          {moment(data.date).format("DD MMMM YYYY, hh:mm A")} •{" "}
          {data.officerType} • Updated by {data.downloadedBy}
        </div>
      </div>

      <div style={{ padding: "20px" }}>
        {/* General Verification */}
        {isSectionMeaningful("generalVerification") && (
          <div style={{ marginBottom: "20px" }}>
            <SectionHeader
              title="General Verification"
              sectionKey="generalVerification"
              bgColor={parsed.generalVerification?.bgColor}
            />
            {isOpen("generalVerification") && (
              <div style={sectionStyle(parsed.generalVerification?.bgColor)}>
                <Field
                  label="Title Owner/ Property Owner Name Verified with Aadharcard Name"
                  value={parsed.generalVerification?.ownerAadharName}
                  showVerified={true}
                />
                <Field
                  label="Borrower Name Verified with Aadharcard Name "
                  value={parsed.generalVerification?.borrowerAadharName}
                  showVerified={true}
                />
                <Field
                  label="Co-Borrower Name / Co-Aplicant Name Verified with AadharCard Name"
                  value={parsed.generalVerification?.coBorrowerAadharName}
                  showVerified={true}
                />
                <Field
                  label="Is The Spelling for Mr./Smt/Minor/Late/ Initial  Correct?"
                  value={parsed.generalVerification?.isSpellingCorrect}
                />
                <Field
                  label="Address of the Property - Hint about File Verified"
                  value={parsed.generalVerification?.addressofProperty}
                />
                <Field
                  label="Nature of the Property (Residential/Commercial/Agri/Industrial)"
                  value={parsed.generalVerification?.natureofProperty}
                />
                <Field
                  label="APP ID Verified"
                  value={parsed.generalVerification?.AppIdverfy}
                />
                <Field
                  label="Constitution"
                  value={parsed.generalVerification?.ownerShip}
                  showVerified={true}
                />
                <Field
                  label="Hint About File Verified"
                  value={parsed.generalVerification?.hintAbtfile}
                />
                <Field
                  label="Freehold/ LeaseHold"
                  value={parsed.generalVerification?.freeRleasehold}
                />
                <SubSection title="File">
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fill, minmax(250px, 1fr))",
                      gap: "15px",
                    }}
                  >
                    <Field
                      label="BT Transfer"
                      value={parsed.generalVerification?.file?.btTransfer}
                    />
                    <Field
                      label="MOD Available"
                      value={parsed.generalVerification?.file?.modAvailable}
                    />
                    <Field
                      label="Purchase"
                      value={parsed.generalVerification?.file?.purchase}
                    />
                    <Field
                      label="Sale Agreement"
                      value={parsed.generalVerification?.file?.saleAgreement}
                    />
                    <Field
                      label="New Building / Construction/Self Construction"
                      value={parsed.generalVerification?.file?.selfConstruction}
                    />
                    <Field
                      label="Proceeding Approval/Approved Plan"
                      value={
                        parsed.generalVerification?.file?.proceedingApproval
                      }
                    />
                    <Field
                      label="Fresh Case"
                      value={parsed.generalVerification?.file?.new}
                    />
                    <Field
                      label="Predisbursement Verified"
                      value={
                        parsed.generalVerification?.file?.preDisbursmentverfy
                      }
                    />
                    <Field
                      label="Post Disbursement Verified"
                      value={parsed.generalVerification?.file?.postDisbursverfy}
                    />
                  </div>
                </SubSection>
              </div>
            )}
          </div>
        )}

        {/* HSD Pattas Verify */}
        {isSectionMeaningful("HsdpattasVerify") && (
          <div style={{ marginBottom: "20px" }}>
            <SectionHeader
              title="HSD Patta Verification (Title Deed)/Assignment Patta Verification"
              sectionKey="HsdpattasVerify"
              bgColor={parsed.HsdpattasVerify?.bgColor}
            />
            {isOpen("HsdpattasVerify") && (
              <div style={sectionStyle(parsed.HsdpattasVerify?.bgColor)}>
                {cleanRows(parsed.HsdpattasVerify?.Hsdpattas).map(
                  (item: any, idx: number) => (
                    <div key={idx} style={{ marginBottom: "30px" }}>
                      <h3
                        style={{
                          margin: "0 0 15px 0",
                          paddingBottom: "8px",
                          borderBottom: "2px solid #007bff",
                          color: "#007bff",
                          fontSize: "15px",
                          fontWeight: 600,
                        }}
                      >
                        HSD Pattas Verify {idx + 1}
                      </h3>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fill, minmax(250px, 1fr))",
                          gap: "15px",
                        }}
                      >
                        <Field
                          label="Road Approaches"
                          value={item?.roadApproachs}
                        />
                        <Field label="Certificate" value={item?.certificate} />
                        <Field
                          label="NOC for Tahsildar"
                          value={item?.nocforTahsildar}
                        />
                        <Field
                          label="HSD Patta Issued by"
                          value={item?.hsdpattaissuedby}
                        />
                        <Field
                          label="Condition Fulfilled"
                          value={item?.conditionFullfilled}
                        />
                        <Field
                          label="Mention Condition"
                          value={item?.mentionCondition}
                        />
                      </div>
                    </div>
                  ),
                )}
              </div>
            )}
          </div>
        )}

        {/* Natham Patta Verify */}
        {isSectionMeaningful("nathamPattaverify") && (
          <div style={{ marginBottom: "20px" }}>
            <SectionHeader
              title="Natham Patta (Title Deed)"
              sectionKey="nathamPattaverify"
              bgColor={parsed.nathamPattaverify?.bgColor}
            />
            {isOpen("nathamPattaverify") && (
              <div style={sectionStyle(parsed.nathamPattaverify?.bgColor)}>
                {cleanRows(parsed.nathamPattaverify?.nathamPatta).map(
                  (item: any, idx: number) => (
                    <div key={idx} style={{ marginBottom: "30px" }}>
                      <h3
                        style={{
                          margin: "0 0 15px 0",
                          paddingBottom: "8px",
                          borderBottom: "2px solid #007bff",
                          color: "#007bff",
                          fontSize: "15px",
                          fontWeight: 600,
                        }}
                      >
                        Natham/Marasamat Pattas Verify {idx + 1}
                      </h3>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fill, minmax(250px, 1fr))",
                          gap: "15px",
                        }}
                      >
                        <Field
                          label="Issued By Special Tahsildar"
                          value={item?.specialTahsildar}
                        />
                        <Field
                          label="Boundary Certificate"
                          value={item?.boundaryCertificate}
                        />
                        <Field
                          label="Road Approach"
                          value={item?.roadApproach}
                        />
                      </div>
                    </div>
                  ),
                )}
              </div>
            )}
          </div>
        )}

        {/* flow */}
        {isSectionMeaningful("flow") && (
          <div style={{ marginBottom: "20px" }}>
            <SectionHeader
              title="Flow"
              sectionKey="flow"
              bgColor={parsed.flow?.bgColor}
            />
            {isOpen("flow") && (
              <div style={sectionStyle(parsed.flow?.bgColor)}>
                {cleanRows(parsed.flow?.flow).map((item: any, idx: number) => (
                  <div key={idx} style={{ marginBottom: "30px" }}>
                    <h3
                      style={{
                        margin: "0 0 15px 0",
                        paddingBottom: "8px",
                        borderBottom: "2px solid #007bff",
                        color: "#007bff",
                        fontSize: "15px",
                        fontWeight: 600,
                      }}
                    >
                      Flow {idx + 1}
                    </h3>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fill, minmax(250px, 1fr))",
                        gap: "15px",
                      }}
                    >
                      <Field
                        label="Chainlink is in Order- Favour and Excecutant is in flow"
                        value={item?.Chainlink}
                      />
                      <Field
                        label="in flow ,document holder expired means Legal Heir Certificate available"
                        value={item?.inFlowdoc}
                      />
                      <Field
                        label="All the Legal Heirs signed in the next document"
                        value={item?.legalHeirsSigndoc}
                      />
                      <Field
                        label="In flow , Court order comes -Verified"
                        value={item?.courtOrderVerify}
                      />
                      <Field
                        label="In flow, POA Verified"
                        value={item?.flowPoAVerfy}
                      />
                      <Field
                        label="In flow Extent compare with the origin document to title deed"
                        value={item?.flowextentDeed}
                      />
                      <Field
                        label="In flow Survey Number compare with the origin document to title deed"
                        value={item?.flowSurveynoDeed}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* propertytax */}
        {isSectionMeaningful("propertytax") && (
          <div style={{ marginBottom: "20px" }}>
            <SectionHeader
              title="Property Tax"
              sectionKey="propertytax"
              bgColor={parsed.propertytax?.bgColor}
            />
            {isOpen("propertytax") && (
              <div style={sectionStyle(parsed.propertytax?.bgColor)}>
                {cleanRows(parsed.propertytax?.propertytax).map(
                  (item: any, idx: number) => (
                    <div key={idx} style={{ marginBottom: "30px" }}>
                      <h3
                        style={{
                          margin: "0 0 15px 0",
                          paddingBottom: "8px",
                          borderBottom: "2px solid #007bff",
                          color: "#007bff",
                          fontSize: "15px",
                          fontWeight: 600,
                        }}
                      >
                        Property Tax {idx + 1}
                      </h3>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fill, minmax(250px, 1fr))",
                          gap: "15px",
                        }}
                      >
                        <Field
                          label="Date Verified"
                          value={item?.dataVerified}
                        />
                        <Field
                          label="Property Owner Name Verified"
                          value={item?.propertOwnerVerify}
                        />
                        <Field
                          label="Amount  Verified"
                          value={item?.propertyAmountverify}
                        />
                        <Field
                          label="Door.No Verified"
                          value={item?.doorNoVerify}
                        />
                        <Field
                          label="Assessment No Verified"
                          value={item?.assessmentVerifi}
                        />
                        <Field
                          label="Property Tax issued by Verified"
                          value={item?.propertyTaxVerify}
                        />
                        <Field
                          label="Property Tax Verified with QR Code"
                          value={item?.propertyTaxVerifyQrcode}
                        />
                        <Field
                          label="Attached Proof Submitted for Verified QR Code"
                          value={item?.attachProofVerifyQrcode}
                        />
                      </div>
                    </div>
                  ),
                )}
              </div>
            )}
          </div>
        )}

        {/* EBreceiptData */}
        {isSectionMeaningful("EBreceiptData") && (
          <div style={{ marginBottom: "20px" }}>
            <SectionHeader
              title="EB Receipt"
              sectionKey="EBreceiptData"
              bgColor={parsed.EBreceiptData?.bgColor}
            />
            {isOpen("EBreceiptData") && (
              <div style={sectionStyle(parsed.EBreceiptData?.bgColor)}>
                {cleanRows(parsed.EBreceiptData?.EBreceipt).map(
                  (item: any, idx: number) => (
                    <div key={idx} style={{ marginBottom: "30px" }}>
                      <h3
                        style={{
                          margin: "0 0 15px 0",
                          paddingBottom: "8px",
                          borderBottom: "2px solid #007bff",
                          color: "#007bff",
                          fontSize: "15px",
                          fontWeight: 600,
                        }}
                      >
                        EB Receipt {idx + 1}
                      </h3>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fill, minmax(250px, 1fr))",
                          gap: "15px",
                        }}
                      >
                        <Field
                          label="Date Verified"
                          value={item?.dateVerified}
                        />
                        <Field
                          label="Property Owner Name Verified"
                          value={item?.propertOwnerVerify}
                        />
                        <Field
                          label="Amount  Verified"
                          value={item?.ebAmountverify}
                        />
                        <Field
                          label="EB Receipt issued by Verified"
                          value={item?.receiptIssuedByVerify}
                        />
                      </div>
                    </div>
                  ),
                )}
              </div>
            )}
          </div>
        )}

        {/* Survey No Verification */}
        {isSectionMeaningful("surveyNoVerification") && (
          <div style={{ marginBottom: "20px" }}>
            <SectionHeader
              title="Survey No Verification"
              sectionKey="surveyNoVerification"
              bgColor={parsed.surveyNoVerification?.bgColor}
            />
            {isOpen("surveyNoVerification") && (
              <div style={sectionStyle(parsed.surveyNoVerification?.bgColor)}>
                {/* Only show register table if its show status is true */}
                {shouldShowSubSection(
                  parsed.surveyNoVerification,
                  "isshowregisterStatus",
                ) && (
                  <Table
                    title="A Register From Patta"
                    data={parsed.surveyNoVerification?.register || []}
                    columns={[
                      { key: "surveyNo", label: "Survey No" },
                      { key: "tslrNo", label: "TSLR No" },
                      { key: "extent", label: "Extent" },
                      { key: "name", label: "Name" },
                      {
                        key: "aRegisterMeanscompare",
                        label:
                          "Origin A Register Means Compare with Title Deed Survey Number",
                      },
                    ]}
                  />
                )}

                {/* Only show titleDeed table if its show status is true */}
                {shouldShowSubSection(
                  parsed.surveyNoVerification,
                  "isshowtitleDeedStatus",
                ) && (
                  <Table
                    title="Title Deed"
                    data={parsed.surveyNoVerification?.titleDeed || []}
                    columns={[
                      { key: "surveyNo", label: "New Survey Number Verified" },
                      {
                        key: "tslrNo",
                        label: "New Survey Number Verified with Patta Extract",
                      },
                      {
                        key: "pattaExtractSno",
                        label: "Patta Extract S.No",
                      },
                      {
                        key: "extent",
                        label:
                          "Title Deed Extent ( Sq.Mt, Sq.Ft ,Cents) Verified",
                      },
                      { key: "name", label: "Name" },
                      {
                        key: "Corelation",
                        label: "Corelation Certificate Verified",
                      },
                      {
                        key: "proceedingpatta",
                        label: "Proceeding of Patta Verified",
                      },
                      {
                        key: "deedExtentverfy",
                        label: "Title Deed Extent Verified With Patta Extract",
                      },
                      {
                        key: "roadAppBound",
                        label: "Road Approach Available in Boundaries",
                      },
                      {
                        key: "pathwayBound",
                        label: "Pathway Available in Boundaries",
                      },
                      {
                        key: "ofbyBoundverfy",
                        label: " Of /BY Boundaries direction Verified",
                      },
                      {
                        key: "boundverfyNorth",
                        label: "Boundary of North Verified",
                      },
                      {
                        key: "boundverfySouth",
                        label: "Boundary of South Verified",
                      },
                      {
                        key: "boundverfyEast",
                        label: "Boundary of East Verified",
                      },
                      {
                        key: "boundverfyWest",
                        label: "Boundary of West Verified",
                      },
                      {
                        key: "northeastMeasur",
                        label: "North- East West Measureements in Sq.Ft/ Sq.Mt",
                      },
                      {
                        key: "southeastMeasur",
                        label:
                          "South- East West  Measurements in   Sq.Ft/ Sq.Mt",
                      },
                      {
                        key: "eastnorthMeasur",
                        label:
                          "East- North South Measurements In  Sq.Ft/ Sq.Mt",
                      },
                      {
                        key: "westnorthMeasur",
                        label: "West- North South Measurements  Sq.Ft/ Sq.Mt",
                      },
                      {
                        key: "measurementextentmatch",
                        label:
                          "Measurement Extent Matched with Title Deed Total Extent",
                      },
                      {
                        key: "landTypeNanjai",
                        label:
                          "Land type is Nanjai (Yes Means Hold/Policy/Approval)",
                      },
                      {
                        key: "NonAgrifromtashidar",
                        label:
                          "Is Title  Extent more than 20 Cents( Non Agri From Tahsildar)",
                      },
                      { key: "deedDoorNoverfy", label: "Door No Verified" },
                      { key: "deedplotNoverfy", label: "Plot No Verified" },
                      {
                        key: "deedlayoutnameverfy",
                        label: "Layout Name Verified ",
                      },
                      {
                        key: "deedstreetnameverfy",
                        label: "Street Name Verified",
                      },
                      {
                        key: "newtalukVerified",
                        label: "New Taluk Verified  with Patta",
                      },
                    ]}
                  />
                )}

                {/* Only show currentPatta table if its show status is true */}
                {shouldShowSubSection(
                  parsed.surveyNoVerification,
                  "isshowcurrentPattaStatus",
                ) && (
                  <Table
                    title="Current Patta/Chitta/TSLR No"
                    data={parsed.surveyNoVerification?.currentPatta || []}
                    columns={[
                      { key: "surveyNo", label: "Survey No" },
                      { key: "tslrNo", label: "TSLR No" },
                      { key: "extent", label: "Extent" },
                      { key: "name", label: "Name" },
                      {
                        key: "governmentLandorNotverify",
                        label: "Government Land or Not-Verified",
                      },
                    ]}
                  />
                )}

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(250px, 1fr))",
                    gap: "15px",
                  }}
                >
                  <Field
                    label="Total Survey No in Title Deed"
                    value={parsed.surveyNoVerification?.totalTitleDeed}
                  />
                  <Field
                    label="Patta Verified with QR Code"
                    value={parsed.surveyNoVerification?.pattaqrcode}
                  />
                  <Field
                    label="Attachment Proof Submitted for Verified QR Code"
                    value={parsed.surveyNoVerification?.proofverifyqrcode}
                  />
                  <Field
                    label="Patta Issued by Verified"
                    value={parsed.surveyNoVerification?.pattaissuedverfy}
                  />
                  <Field
                    label="Total Survey No in EC"
                    value={parsed.surveyNoVerification?.totalEc}
                  />
                  <Field
                    label="Difference"
                    value={parsed.surveyNoVerification?.difference}
                  />
                  <Field
                    label="Missing Survey No in EC"
                    value={parsed.surveyNoVerification?.missingEc}
                  />
                  <Field
                    label="Land Type  (Yes Means Hold/Policy/Approval)"
                    value={
                      parsed.surveyNoVerification?.type ? "Nanjai" : "Punjai"
                    }
                  />
                  <Field
                    label="Condition Patta"
                    value={parsed.surveyNoVerification?.conditionPatta}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Deed - Full implementation of all 10 deed types */}
        {isSectionMeaningful("deed") && (
          <div style={{ marginBottom: "20px" }}>
            <SectionHeader
              title="Deed"
              sectionKey="deed"
              bgColor={parsed.deed?.bgColor}
            />
            {isOpen("deed") && (
              <div style={sectionStyle(parsed.deed?.bgColor)}>
                {/* Sale Deed - Only show if its show status is true */}
                {shouldShowSubSection(parsed.deed, "isshowsaleDeedStatus") &&
                  parsed.deed?.saleDeed?.some(isMeaningfulRow) &&
                  cleanRows(parsed.deed.saleDeed).map(
                    (item: any, idx: number) => (
                      <SubSection key={`sale-${idx}`} title="Sale Deed">
                        <div style={{ marginBottom: "30px" }}>
                          <h3
                            style={{
                              margin: "0 0 15px 0",
                              paddingBottom: "8px",
                              borderBottom: "2px solid #007bff",
                              color: "#007bff",
                              fontSize: "15px",
                              fontWeight: 600,
                            }}
                          >
                            Sale Deed {idx + 1}
                          </h3>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "repeat(2, 1fr)",
                              gap: "15px",
                            }}
                          >
                            <Field
                              label="Amount Fully Transferred?"
                              value={item?.saleDeedAmountTransfered}
                            />
                            <Field
                              label="Conditions Mentioned or Not?"
                              value={item?.saleDeedConditionsMentioned}
                            />
                          </div>
                        </div>
                      </SubSection>
                    ),
                  )}

                {/* Gift Settlement Deed - Only show if its show status is true */}
                {shouldShowSubSection(parsed.deed, "isshowgiftDeedStatus") &&
                  parsed.deed?.giftDeed?.some(isMeaningfulRow) &&
                  cleanRows(parsed.deed.giftDeed).map(
                    (item: any, idx: number) => (
                      <SubSection key={`gift-${idx}`} title="Gift Settlement Deed">
                        <div style={{ marginBottom: "30px" }}>
                          <h3
                            style={{
                              margin: "0 0 15px 0",
                              paddingBottom: "8px",
                              borderBottom: "2px solid #007bff",
                              color: "#007bff",
                              fontSize: "15px",
                              fontWeight: 600,
                            }}
                          >
                            Gift Settlement Deed {idx + 1}
                          </h3>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns:
                                "repeat(auto-fill, minmax(250px, 1fr))",
                              gap: "15px",
                            }}
                          >
                            <Field label="Derived By" value={item?.derivedBy} />
                            <Field
                              label="Registered?"
                              value={item?.registered}
                            />
                            <Field label="Condition?" value={item?.condition} />
                            <Field
                              label="Condition Fulfilled?"
                              value={item?.conditionFulfilled}
                            />
                            <Field
                              label="Mention Condition"
                              value={item?.mentionCondition}
                            />
                          </div>
                        </div>
                      </SubSection>
                    ),
                  )}

                {/* Settlement Deed */}
                {shouldShowSubSection(
                  parsed.deed,
                  "isshowsettlementDeedStatus",
                ) &&
                  parsed.deed?.settlementDeed?.some(isMeaningfulRow) &&
                  cleanRows(parsed.deed.settlementDeed).map(
                    (item: any, idx: number) => (
                      <SubSection
                        key={`settlement-${idx}`}
                        title="Settlement Deed"
                      >
                        <div style={{ marginBottom: "30px" }}>
                          <h3
                            style={{
                              margin: "0 0 15px 0",
                              paddingBottom: "8px",
                              borderBottom: "2px solid #007bff",
                              color: "#007bff",
                              fontSize: "15px",
                              fontWeight: 600,
                            }}
                          >
                            Settlement Deed {idx + 1}
                          </h3>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns:
                                "repeat(auto-fill, minmax(250px, 1fr))",
                              gap: "15px",
                            }}
                          >
                            <Field label="Derived By" value={item?.derivedBy} />
                            <Field
                              label="Registered?"
                              value={item?.registered}
                            />
                            <Field label="Condition?" value={item?.condition} />
                            <Field
                              label="Condition Fulfilled?"
                              value={item?.conditionFulfilled}
                            />
                            <Field
                              label="Death Certificate Obtained "
                              value={item?.deathcertificateobtained}
                            />
                            <Field
                              label="Relationship Mentioned?"
                              value={item?.relationshipMentioned}
                            />
                            <Field
                              label="Mention Condition"
                              value={item?.mentionCondition}
                            />
                          </div>
                        </div>
                      </SubSection>
                    ),
                  )}

                {/* Partition Deed */}
                {shouldShowSubSection(
                  parsed.deed,
                  "isshowpartitionDeedStatus",
                ) &&
                  parsed.deed?.partitionDeed?.some(isMeaningfulRow) &&
                  cleanRows(parsed.deed.partitionDeed).map(
                    (item: any, idx: number) => (
                      <SubSection
                        key={`partition-${idx}`}
                        title="Release or Partition Release Deed"
                      >
                        <div style={{ marginBottom: "30px" }}>
                          <h3
                            style={{
                              margin: "0 0 15px 0",
                              paddingBottom: "8px",
                              borderBottom: "2px solid #007bff",
                              color: "#007bff",
                              fontSize: "15px",
                              fontWeight: 600,
                            }}
                          >
                            Release or Partition Release Deed {idx + 1}
                          </h3>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns:
                                "repeat(auto-fill, minmax(250px, 1fr))",
                              gap: "15px",
                            }}
                          >
                            <Field label="Derived By" value={item?.derivedBy} />
                            <Field
                              label="Registered?"
                              value={item?.registered}
                            />
                            <Field label="Condition?" value={item?.condition} />
                            <Field
                              label="Condition Fulfilled?"
                              value={item?.conditionFulfilled}
                            />
                            <Field
                              label="Mention Condition"
                              value={item?.mentionCondition}
                            />
                            <Field
                              label="Doc Alloted For Mortgage"
                              value={
                                item?.partionscheduleMortage ??
                                item?.docAllotedForMortgage
                              }
                            />
                            <Field
                              label="Original Copy Available"
                              value={item?.originalCopyAvailable}
                            />
                          </div>
                        </div>
                      </SubSection>
                    ),
                  )}

                {/* Will Deed */}
                {shouldShowSubSection(parsed.deed, "isshowwillDeedStatus") &&
                  parsed.deed?.willDeed?.some(isMeaningfulRow) &&
                  cleanRows(parsed.deed.willDeed).map(
                    (item: any, idx: number) => (
                      <SubSection key={`will-${idx}`} title="Will Deed">
                        <div style={{ marginBottom: "30px" }}>
                          <h3
                            style={{
                              margin: "0 0 15px 0",
                              paddingBottom: "8px",
                              borderBottom: "2px solid #007bff",
                              color: "#007bff",
                              fontSize: "15px",
                              fontWeight: 600,
                            }}
                          >
                            Will Deed {idx + 1}
                          </h3>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns:
                                "repeat(auto-fill, minmax(250px, 1fr))",
                              gap: "15px",
                            }}
                          >
                            <Field label="Derived By" value={item?.derivedBy} />
                            <Field
                              label="Registered?"
                              value={item?.registered}
                            />
                            <Field label="Condition?" value={item?.condition} />
                            <Field
                              label="Condition Fulfilled?"
                              value={item?.conditionFulfilled}
                            />
                            <Field
                              label="Mention Condition"
                              value={item?.mentionCondition}
                            />
                            <Field
                              label="Cerified Copy Verified"
                              value={item?.willdeedcertcopyverify}
                            />
                            <Field
                              label="Death Certificate Obtained "
                              value={item?.willdeaddeathcert}
                            />
                            <Field
                              label="Relationship Mentioned"
                              value={item?.willdeadreationship}
                            />
                          </div>
                        </div>
                      </SubSection>
                    ),
                  )}

                {/* Lease Deed */}
                {shouldShowSubSection(parsed.deed, "isshowleaseDeedStatus") &&
                  parsed.deed?.leaseDeed?.some(isMeaningfulRow) &&
                  cleanRows(parsed.deed.leaseDeed).map(
                    (item: any, idx: number) => (
                      <SubSection key={`lease-${idx}`} title="Lease Deed">
                        <div style={{ marginBottom: "30px" }}>
                          <h3
                            style={{
                              margin: "0 0 15px 0",
                              paddingBottom: "8px",
                              borderBottom: "2px solid #007bff",
                              color: "#007bff",
                              fontSize: "15px",
                              fontWeight: 600,
                            }}
                          >
                            Lease Deed {idx + 1}
                          </h3>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns:
                                "repeat(auto-fill, minmax(250px, 1fr))",
                              gap: "15px",
                            }}
                          >
                            <Field
                              label="Lease Period From Date"
                              value={item?.leasePeriodFromDate}
                            />
                            <Field
                              label="Lease Period To Date"
                              value={item?.leasePeriodToDate}
                            />
                            <Field
                              label="NOC Available?"
                              value={item?.nocAvailable}
                            />
                            <Field
                              label="Is there a word  to empower mortgage in Lease Deed"
                              value={item?.leaseDeedempowermortage}
                            />
                            <Field
                              label="Lease Cancellation Available"
                              value={item?.leaseDeedCancellation}
                            />
                          </div>
                        </div>
                      </SubSection>
                    ),
                  )}

                {/* Attorney Deed */}
                {shouldShowSubSection(
                  parsed.deed,
                  "isshowattorneyDeedStatus",
                ) &&
                  parsed.deed?.attorneyDeed?.some(isMeaningfulRow) &&
                  cleanRows(parsed.deed.attorneyDeed).map(
                    (item: any, idx: number) => (
                      <SubSection key={`attorney-${idx}`} title="Attorney Deed">
                        <div style={{ marginBottom: "30px" }}>
                          <h3
                            style={{
                              margin: "0 0 15px 0",
                              paddingBottom: "8px",
                              borderBottom: "2px solid #007bff",
                              color: "#007bff",
                              fontSize: "15px",
                              fontWeight: 600,
                            }}
                          >
                            Attorney Deed {idx + 1}
                          </h3>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns:
                                "repeat(auto-fill, minmax(250px, 1fr))",
                              gap: "15px",
                            }}
                          >
                            <Field label="Derived By" value={item?.derivedBy} />
                            <Field
                              label="Registered?"
                              value={item?.registered}
                            />
                            <Field
                              label="Is In Force?"
                              value={item?.isInForce}
                            />
                            <Field
                              label="Is Certified Copy Available?"
                              value={item?.isCertifiedCopyAvailable}
                            />
                            <Field
                              label="Is there a word  to empower mortgage in power Document"
                              value={item?.attorneyDeedmortage}
                            />
                            <Field
                              label="Sale Receipt -Subsequent Deed available or not"
                              value={item?.attorneyDeedsaleReceipt}
                            />
                            <Field
                              label="Life Certificate is available for current Transfer(30 days Validity)"
                              value={item?.attorneyLifecert}
                            />
                          </div>
                        </div>
                      </SubSection>
                    ),
                  )}

                {/* Release Deed */}
                {shouldShowSubSection(parsed.deed, "isshowreleaseDeedStatus") &&
                  parsed.deed?.releaseDeed?.some(isMeaningfulRow) &&
                  cleanRows(parsed.deed.releaseDeed).map(
                    (item: any, idx: number) => (
                      <SubSection key={`release-${idx}`} title="Release Deed">
                        <div style={{ marginBottom: "30px" }}>
                          <h3
                            style={{
                              margin: "0 0 15px 0",
                              paddingBottom: "8px",
                              borderBottom: "2px solid #007bff",
                              color: "#007bff",
                              fontSize: "15px",
                              fontWeight: 600,
                            }}
                          >
                            Release Deed {idx + 1}
                          </h3>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns:
                                "repeat(auto-fill, minmax(250px, 1fr))",
                              gap: "15px",
                            }}
                          >
                            <Field label="Derived By" value={item?.derivedBy} />
                            <Field
                              label="Registered?"
                              value={item?.registered}
                            />
                            <Field label="Condition?" value={item?.condition} />
                            <Field label="Fulfilled?" value={item?.fulFilled} />
                            <Field
                              label="Property Share"
                              value={item?.propertyshare}
                            />
                            <Field
                              label="Mention Condition"
                              value={item?.mentionCondition}
                            />
                            <Field
                              label="Death Certificate Obtained ?"
                              value={item?.deatcertficate}
                            />
                            <Field
                              label="Relationship Mentioned?"
                              value={item?.relationShipmentioned}
                            />
                          </div>
                        </div>
                      </SubSection>
                    ),
                  )}

                {/* Rectification Deed */}
                {shouldShowSubSection(
                  parsed.deed,
                  "isshowrectificationDeedStatus",
                ) &&
                  parsed.deed?.rectificationDeed?.some(isMeaningfulRow) &&
                  cleanRows(parsed.deed.rectificationDeed).map(
                    (item: any, idx: number) => (
                      <SubSection
                        key={`rectification-${idx}`}
                        title="Rectification Deed"
                      >
                        <div style={{ marginBottom: "30px" }}>
                          <h3
                            style={{
                              margin: "0 0 15px 0",
                              paddingBottom: "8px",
                              borderBottom: "2px solid #007bff",
                              color: "#007bff",
                              fontSize: "15px",
                              fontWeight: 600,
                            }}
                          >
                            Rectification Deed {idx + 1}
                          </h3>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns:
                                "repeat(auto-fill, minmax(250px, 1fr))",
                              gap: "15px",
                            }}
                          >
                            <Field label="Derived By" value={item?.derivedBy} />
                            <Field
                              label="Registered?"
                              value={item?.registered}
                            />
                            <Field
                              label="Wrong Mentioned Details"
                              value={item?.mentionDetails}
                            />
                            <Field
                              label="Correct Details"
                              value={item?.correctDetails}
                            />
                          </div>
                        </div>
                      </SubSection>
                    ),
                  )}

                {/* Consent Deed */}
                {shouldShowSubSection(parsed.deed, "isshowconsentDeedStatus") &&
                  parsed.deed?.consentDeed?.some(isMeaningfulRow) &&
                  cleanRows(parsed.deed.consentDeed).map(
                    (item: any, idx: number) => (
                      <SubSection key={`consent-${idx}`} title="Consent Deed">
                        <div style={{ marginBottom: "30px" }}>
                          <h3
                            style={{
                              margin: "0 0 15px 0",
                              paddingBottom: "8px",
                              borderBottom: "2px solid #007bff",
                              color: "#007bff",
                              fontSize: "15px",
                              fontWeight: 600,
                            }}
                          >
                            Consent Deed {idx + 1}
                          </h3>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns:
                                "repeat(auto-fill, minmax(250px, 1fr))",
                              gap: "15px",
                            }}
                          >
                            <Field label="Derived By" value={item?.derivedBy} />
                            <Field
                              label="Registered?"
                              value={item?.registered}
                            />
                            <Field
                              label="Age checked with Aadharcard"
                              value={item?.agecheckAadhar}
                            />
                            <Field label="Reason" value={item?.reason} />
                          </div>
                        </div>
                      </SubSection>
                    ),
                  )}

                {/* Missing Document */}
                {shouldShowSubSection(
                  parsed.deed,
                  "isshowmissingDocumentStatus",
                ) &&
                  parsed.deed?.missingDocument?.some(isMeaningfulRow) &&
                  cleanRows(parsed.deed.missingDocument).map(
                    (item: any, idx: number) => (
                      <SubSection
                        key={`missing-${idx}`}
                        title="Missing Document"
                      >
                        <div style={{ marginBottom: "30px" }}>
                          <h3
                            style={{
                              margin: "0 0 15px 0",
                              paddingBottom: "8px",
                              borderBottom: "2px solid #007bff",
                              color: "#007bff",
                              fontSize: "15px",
                              fontWeight: 600,
                            }}
                          >
                            Missing Document {idx + 1}
                          </h3>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns:
                                "repeat(auto-fill, minmax(250px, 1fr))",
                              gap: "15px",
                            }}
                          >
                            <Field
                              label="Police Complaint "
                              value={item?.policeComplaint}
                            />
                            <Field
                              label="Paper Ad ( Tamil/ English)"
                              value={item?.paperAd}
                            />
                            <Field
                              label="Non Tracable certificate by Police"
                              value={item?.tracableCertificate}
                            />
                            <Field
                              label="No Claim Certificate by Advocate"
                              value={item?.NoClaimcertificate}
                            />
                          </div>
                        </div>
                      </SubSection>
                    ),
                  )}

                {/* Gazette */}
                {shouldShowSubSection(parsed.deed, "ishowGazettestatus") &&
                  (isMeaningfulValue(
                    parsed.deed?.gazetteofDoc?.isNameChanged,
                  ) ||
                    isMeaningfulValue(
                      parsed.deed?.gazetteofDoc?.gazettePublication,
                    ) ||
                    isMeaningfulValue(
                      parsed.deed?.gazetteofDoc?.NameChangedbythasildar,
                    ) ||
                    isMeaningfulValue(
                      parsed.deed?.gazetteofDoc?.IndementyBond,
                    ) ||
                    isMeaningfulValue(
                      parsed.deed?.gazetteofDoc?.docList,
                    )) && (
                    <SubSection title="Gazette">
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fill, minmax(250px, 1fr))",
                          gap: "15px",
                        }}
                      >
                        <Field
                          label="Is Name Changed(Gazette)"
                          value={parsed.deed?.gazetteofDoc?.isNameChanged}
                        />
                        <Field
                          label="Gazette Publication available?"
                          value={
                            parsed.deed?.gazetteofDoc?.gazettePublication
                          }
                        />
                        <Field
                          label="Name Changed Certifcate by Tahsildar/VAO"
                          value={
                            parsed.deed?.gazetteofDoc
                              ?.NameChangedbythasildar
                          }
                        />
                        <Field
                          label="Indementy Bond/Affidavit"
                          value={parsed.deed?.gazetteofDoc?.IndementyBond}
                        />
                        <Field
                          label="Name Changed Document(s) List?"
                          value={parsed.deed?.gazetteofDoc?.docList}
                        />
                      </div>
                    </SubSection>
                  )}

                {/* Plot Regularization - Only show if its show status is true */}
                {shouldShowSubSection(
                  parsed.deed,
                  "ishowplotregulationstatus",
                ) &&
                  (isMeaningfulValue(
                    parsed.deed?.plotRegularization
                      ?.isDeedRegisteredAfter30_9_2016,
                  ) ||
                    isMeaningfulValue(
                      parsed.deed?.plotRegularization?.isPlotObtained,
                    )) && (
                    <SubSection title="Plot Regularization">
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fill, minmax(250px, 1fr))",
                          gap: "15px",
                        }}
                      >
                        <Field
                          label="Is Deed Registered After 30-09-2016?"
                          value={
                            parsed.deed?.plotRegularization
                              ?.isDeedRegisteredAfter30_9_2016
                          }
                        />
                        <Field
                          label="Is Plot Regulation Obtained?"
                          value={
                            parsed.deed?.plotRegularization?.isPlotObtained
                          }
                        />
                      </div>
                    </SubSection>
                  )}

                {/* Adjudication Power Deed - Only show if its show status is true */}
                {shouldShowSubSection(parsed.deed, "ishowAdjudicationstatus") &&
                  (isMeaningfulValue(
                    parsed.deed?.foriegnPower?.isForiegnPowerRegistered,
                  ) ||
                    isMeaningfulValue(parsed.deed?.foriegnPower?.docNo)) && (
                    <SubSection title="Adjudication Power Deed">
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fill, minmax(250px, 1fr))",
                          gap: "15px",
                        }}
                      >
                        <Field
                          label="Is Adjudication Power Registered??"
                          value={
                            parsed.deed?.foriegnPower?.isForiegnPowerRegistered
                          }
                        />
                        <Field
                          label="Doc No"
                          value={parsed.deed?.foriegnPower?.docNo || "—"}
                        />
                      </div>
                    </SubSection>
                  )}

                {isMeaningfulValue(parsed.deed?.mortgageInPowerDoc) && (
                  <Field
                    label="Is there a word to Empowerr to Mortgage "
                    value={parsed.deed?.mortgageInPowerDoc}
                  />
                )}
              </div>
            )}
          </div>
        )}

        {/* Legal Heirs Verify */}
        {isSectionMeaningful("legalHeirsVerify") && (
          <div style={{ marginBottom: "20px" }}>
            <SectionHeader
              title="Legal Heirs Verification"
              sectionKey="legalHeirsVerify"
              bgColor={parsed.legalHeirsVerify?.bgColor}
            />
            {isOpen("legalHeirsVerify") && (
              <div style={sectionStyle(parsed.legalHeirsVerify?.bgColor)}>
                {cleanRows(parsed.legalHeirsVerify?.legalHeirs).map(
                  (item: any, idx: number) => (
                    <div key={idx} style={{ marginBottom: "30px" }}>
                      <h3
                        style={{
                          margin: "0 0 15px 0",
                          paddingBottom: "8px",
                          borderBottom: "2px solid #007bff",
                          color: "#007bff",
                          fontSize: "15px",
                          fontWeight: 600,
                        }}
                      >
                        Legal Heirs Verify {idx + 1}
                      </h3>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fill, minmax(250px, 1fr))",
                          gap: "15px",
                        }}
                      >
                        <Field
                          label="Legal Heirs Within 13 Years Period?"
                          value={item?.legalHeirsWithin13}
                        />
                        <Field
                          label="Predeceased Son/Daughter Legal Heirs Verified"
                          value={item?.PredeceasedlegalHeire}
                        />
                        <Field
                          label="Legal Heir Certificate Verified with QR Code"
                          value={item?.legalHeirsWithqrcode}
                        />
                        <Field
                          label="Attached Proof Submitted for Verified QR Code"
                          value={item?.legalHeirsattacedproof}
                        />
                        <Field
                          label="Legal Heirs Certificate Number & Year"
                          value={item?.docNoAndYear}
                        />
                        <Field
                          label="Next Deed Doc No & Year"
                          value={item?.nextDeedDocNoAndYear}
                        />
                        <Field
                          label="Legal Heirs Obtained From"
                          value={item?.legalHeirsObtained}
                        />
                        <Field
                          label="Genealogy Obtained from"
                          value={item?.geanology}
                        />
                      </div>
                    </div>
                  ),
                )}
              </div>
            )}
          </div>
        )}

        {/* EC Verify */}
        {isSectionMeaningful("ecVerify") && (
          <div style={{ marginBottom: "20px" }}>
            <SectionHeader
              title="EC Verification"
              sectionKey="ecVerify"
              bgColor={parsed.ecVerify?.bgColor}
            />
            {isOpen("ecVerify") && (
              <div style={sectionStyle(parsed.ecVerify?.bgColor)}>
                {cleanRows(parsed.ecVerify?.ecentry).map(
                  (item: any, idx: number) => (
                    <div key={idx} style={{ marginBottom: "30px" }}>
                      <h3
                        style={{
                          margin: "0 0 15px 0",
                          paddingBottom: "8px",
                          borderBottom: "2px solid #007bff",
                          color: "#007bff",
                          fontSize: "15px",
                          fontWeight: 600,
                        }}
                      >
                        EC Verify {idx + 1}
                      </h3>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fill, minmax(250px, 1fr))",
                          gap: "15px",
                        }}
                      >
                        <Field
                          label="No Of EC Entries"
                          value={item?.noOfEcEntries}
                        />
                        <Field
                          label="Related EC Entries"
                          value={item?.relatedEcEntries}
                        />
                        <Field
                          label="Not Related Verified?"
                          value={item?.notRelatedVerified}
                        />
                        <Field
                          label="SRO EC Verified with QR Code?"
                          value={item?.onlineEcVerified}
                        />
                        <Field
                          label="Court Attachment"
                          value={item?.isCourtAttachment}
                        />
                        <Field
                          label="Attachment Canceled"
                          value={item?.isAttachmentCanceled}
                        />
                        <Field label="Lease In EC" value={item?.leaseInEc} />
                        <Field
                          label="Lease Cancellation"
                          value={item?.leaseCancellation}
                        />
                        <Field
                          label="Mortgage In EC"
                          value={item?.isMortgageInEc}
                        />
                        <Field
                          label="Receipt In EC"
                          value={item?.isReceiptInEc}
                        />
                        <Field
                          label="Agreement In EC"
                          value={item?.isAgreementInEc}
                        />
                        <Field
                          label="Agreement Canceled"
                          value={item?.isAgreementCanceled}
                        />
                        <Field
                          label="Larger Extent"
                          value={item?.largerExtent}
                        />
                        <Field
                          label="Non Related S No"
                          value={item?.nonRelatedSNo}
                        />
                        <Field
                          label="Different Plot No"
                          value={item?.differentPlotNo}
                        />
                        <Field
                          label="Property Details( S.No/Extent/Plot No) Verified"
                          value={item?.propertdetailsverfy}
                        />
                        <Field
                          label="Period Continuity upto date Verified"
                          value={item?.periodcontinuityverfy}
                        />
                        <Field
                          label="Attached Proof Submitted for Verified QR Code"
                          value={item?.attcheProofVerifyQrcode}
                        />
                        <Field
                          label="Same Plot No/Same Extent /Same S.No/or Title Holder or Previous Title Holder found  in EC entries"
                          value={item?.holderECentries}
                        />
                        <Field
                          label="Any Index Error Found"
                          value={item?.indexerrorfound}
                        />
                        <Field
                          label="Double Entry (Document Number/Plot No/Extent)"
                          value={item?.doubleEntry}
                        />
                        <Field
                          label="EC Page Missing Verified"
                          value={item?.ecPageMIssing}
                        />
                      </div>
                    </div>
                  ),
                )}
              </div>
            )}
          </div>
        )}

        {/* Missing LOD */}
        {isSectionMeaningful("missingLOD") && (
          <div style={{ marginBottom: "20px" }}>
            <SectionHeader
              title="Missing Sheets in LOD"
              sectionKey="missingLOD"
              bgColor={parsed.missingLOD?.bgColor}
            />
            {isOpen("missingLOD") && (
              <div style={sectionStyle(parsed.missingLOD?.bgColor)}>
                <Table
                  data={parsed.missingLOD?.sheets || []}
                  columns={[
                    { key: "fileName", label: "Deed Name" },
                    { key: "fileNo", label: "Doc No" },
                    { key: "totalSheets", label: "Total Sheets" },
                    { key: "availableSheets", label: "Available" },
                    { key: "missingSheets", label: "Missing" },
                    { key: "isCertificateAttached", label: "Certificate Copy" },
                  ]}
                />
              </div>
            )}
          </div>
        )}

        {/* Verification */}
        {isSectionMeaningful("verification") && (
          <div style={{ marginBottom: "20px" }}>
            <SectionHeader
              title="Verification"
              sectionKey="verification"
              bgColor={parsed.verification?.bgColor}
            />
            {isOpen("verification") && (
              <div style={sectionStyle(parsed.verification?.bgColor)}>
                {/* Company - Only show if its show status is true */}
                {shouldShowSubSection(
                  parsed.verification,
                  "isshowcompanyStatus",
                ) && (
                  <SubSection title="Company">
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fill, minmax(250px, 1fr))",
                        gap: "15px",
                      }}
                    >
                      <Field
                        label="Board Resolution"
                        value={parsed.verification?.company?.boardResolution}
                      />
                      <Field
                        label="All Directors Signed In?"
                        value={
                          parsed.verification?.company?.allDirectorsSignedIn
                        }
                      />
                      <Field
                        label="Mortage Permit  in MOA & AOA "
                        value={parsed.verification?.company?.mortagepermit}
                      />
                      <Field
                        label=" MOA & AOA "
                        value={parsed.verification?.company?.MoaAndAoa}
                      />
                      <Field
                        label="Cert of Incorporation Available"
                        value={parsed.verification?.company?.certOfInc}
                      />
                    </div>
                  </SubSection>
                )}

                {/* Trust - Only show if its show status is true */}
                {shouldShowSubSection(
                  parsed.verification,
                  "isshowtrustStatus",
                ) && (
                  <SubSection title="Trust">
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fill, minmax(250px, 1fr))",
                        gap: "15px",
                      }}
                    >
                      <Field
                        label="Trust Deed Available"
                        value={parsed.verification?.trust?.isDeed}
                      />
                      <Field
                        label="Supplimentary Trust Deed Available"
                        value={parsed.verification?.trust?.isSupplement}
                      />
                      <Field
                        label="Board of Resolution Signed ?"
                        value={parsed.verification?.trust?.isResolutionSigned}
                      />
                      <Field
                        label="Affidavit from all the Trustees Signed"
                        value={parsed.verification?.trust?.isAffidavitSigned}
                      />
                    </div>
                  </SubSection>
                )}

                {/* Partnership - Only show if its show status is true */}
                {shouldShowSubSection(
                  parsed.verification,
                  "isshowpartnershipStatus",
                ) && (
                  <SubSection title="Partnership">
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fill, minmax(250px, 1fr))",
                        gap: "15px",
                      }}
                    >
                      <Field
                        label=" Partnership Deed Available"
                        value={
                          parsed.verification?.partnership?.isPartnershipDeed
                        }
                      />
                      <Field
                        label=" Supplimentary Partnership Deed Available"
                        value={
                          parsed.verification?.partnership
                            ?.issupplimentaryPartnershipDeed
                        }
                      />
                      <Field
                        label=" Is there a word to Empowerr to Mortgage"
                        value={
                          parsed.verification?.partnership
                            ?.wordtoempowerrmortage
                        }
                      />
                      <Field
                        label=" Letter Obtained from Partners to Mortgage"
                        value={
                          parsed.verification?.partnership
                            ?.letterObtainpartenermortage
                        }
                      />
                      <Field
                        label="Form C"
                        value={parsed.verification?.partnership?.isFormC}
                      />
                      <Field
                        label="Retire Partner"
                        value={
                          parsed.verification?.partnership?.isRetirePartner
                        }
                      />
                      <Field
                        label="Add Of Partner"
                        value={parsed.verification?.partnership?.addOfPartner}
                      />
                      <Field
                        label="Letter Obtained"
                        value={parsed.verification?.partnership?.letterObtained}
                      />
                      <Field
                        label="Single Partner In Deed"
                        value={
                          parsed.verification?.partnership?.singlePartnerInDeed
                        }
                      />
                    </div>
                  </SubSection>
                )}

                {/* Association - Only show if its show status is true */}
                {shouldShowSubSection(
                  parsed.verification,
                  "isshowassociationStatus",
                ) && (
                  <SubSection title="Association">
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fill, minmax(250px, 1fr))",
                        gap: "15px",
                      }}
                    >
                      <Field
                        label="Association Law"
                        value={
                          parsed.verification?.association?.isAssociationLaw
                        }
                      />
                      <Field
                        label="Board of Resolution"
                        value={parsed.verification?.association?.isResolution}
                      />
                      <Field
                        label="Power To Sign Execution"
                        value={
                          parsed.verification?.association?.powerToSignExecution
                        }
                      />
                    </div>
                  </SubSection>
                )}

                {/* Society - Only show if its show status is true */}
                {shouldShowSubSection(
                  parsed.verification,
                  "isshowsocietyStatus",
                ) && (
                  <SubSection title="Society">
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fill, minmax(250px, 1fr))",
                        gap: "15px",
                      }}
                    >
                      <Field
                        label="MOA"
                        value={parsed.verification?.society?.isMOA}
                      />
                      <Field
                        label="Society Registered"
                        value={
                          parsed.verification?.society?.isSocietyRegistered
                        }
                      />
                      <Field
                        label="Board of Resolution Society Signed?"
                        value={
                          parsed.verification?.society
                            ?.isResolutionSocietySigned
                        }
                      />
                      <Field
                        label="Bylaw of Society"
                        value={parsed.verification?.society?.isBylaw}
                      />
                      <Field
                        label="Power to Mortgage and Sale"
                        value={parsed.verification?.society?.isMortagesale}
                      />
                    </div>
                  </SubSection>
                )}

                {/* Apartment - Only show if its show status is true */}
                {shouldShowSubSection(
                  parsed.verification,
                  "isshowappartmentStatus",
                ) && (
                  <SubSection title="Apartment">
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fill, minmax(250px, 1fr))",
                        gap: "15px",
                      }}
                    >
                      <Field
                        label="UDS"
                        value={parsed.verification?.appartment?.isUDS}
                      />
                      <Field
                        label="Building Floor"
                        value={parsed.verification?.appartment?.isBuildingFloor}
                      />
                      <Field
                        label="Extent"
                        value={parsed.verification?.appartment?.isExtent}
                      />
                    </div>
                  </SubSection>
                )}
              </div>
            )}
          </div>
        )}

        {/* Court Doc Verification */}
        {isSectionMeaningful("courtDocVerification") && (
          <div style={{ marginBottom: "20px" }}>
            <SectionHeader
              title="Court Document Verification"
              sectionKey="courtDocVerification"
              bgColor={parsed.courtDocVerification?.bgColor}
            />
            {isOpen("courtDocVerification") && (
              <div style={sectionStyle(parsed.courtDocVerification?.bgColor)}>
                {/* Partition Suit - Only show if its show status is true */}
                {shouldShowSubSection(
                  parsed.courtDocVerification,
                  "isshowPartitionSuitStatus",
                ) && (
                  <SubSection title="Partition Suit">
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fill, minmax(250px, 1fr))",
                        gap: "15px",
                      }}
                    >
                      <Field
                        label="Final Decree"
                        value={
                          parsed.courtDocVerification?.partitionSuit
                            ?.isFinalDecree
                        }
                      />
                      <Field
                        label="Preliminary Decree"
                        value={
                          parsed.courtDocVerification?.partitionSuit
                            ?.isPreliminaryDecree
                        }
                      />
                      <Field
                        label="Compromise Decree"
                        value={
                          parsed.courtDocVerification?.partitionSuit
                            ?.isCompormiseDecree
                        }
                      />
                      <Field
                        label="Exparty Decree"
                        value={
                          parsed.courtDocVerification?.partitionSuit
                            ?.isExpartyDecree
                        }
                      />
                      <Field
                        label="Contested Decree"
                        value={
                          parsed.courtDocVerification?.partitionSuit
                            ?.ContestedDecree
                        }
                      />
                    </div>
                  </SubSection>
                )}

                {/* Declaration Suit - Only show if its show status is true */}
                {shouldShowSubSection(
                  parsed.courtDocVerification,
                  "isshowDeclarationSuitStatus",
                ) && (
                  <SubSection title="Declaration Suit (Permanent/Temporary Injunction)">
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fill, minmax(250px, 1fr))",
                        gap: "15px",
                      }}
                    >
                      <Field
                        label="Is IA Available?"
                        value={
                          parsed.courtDocVerification?.declarationSuit
                            ?.isIAAvailable
                        }
                      />
                      <Field
                        label="Contest"
                        value={
                          parsed.courtDocVerification?.declarationSuit
                            ?.isContest
                        }
                      />
                      <Field
                        label="Compromise Decree"
                        value={
                          parsed.courtDocVerification?.declarationSuit
                            ?.isCompormiseDecree
                        }
                      />
                      <Field
                        label="Exparty Decree"
                        value={
                          parsed.courtDocVerification?.declarationSuit
                            ?.isExpartyDecree
                        }
                      />
                    </div>
                  </SubSection>
                )}

                {/* Lok Adalat - Only show if its show status is true */}
                {shouldShowSubSection(
                  parsed.courtDocVerification,
                  "isshowLokAdalatStatus",
                ) && (
                  <SubSection title="Lok Adalat">
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fill, minmax(250px, 1fr))",
                        gap: "15px",
                      }}
                    >
                      <Field
                        label="Old Court Name"
                        value={
                          parsed.courtDocVerification?.lokAdalat?.oldCourtName
                        }
                      />
                      <Field
                        label="Case No"
                        value={parsed.courtDocVerification?.lokAdalat?.caseNo}
                      />
                      <Field
                        label="Lok Adalat Case No"
                        value={
                          parsed.courtDocVerification?.lokAdalat
                            ?.lokAdalatCaseNo
                        }
                      />
                    </div>
                  </SubSection>
                )}

                {/* Specific Performance Suit - Only show if its show status is true */}
                {shouldShowSubSection(
                  parsed.courtDocVerification,
                  "isshowSpecificSuitStatus",
                ) && (
                  <SubSection title="Specific Performance Suit">
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fill, minmax(250px, 1fr))",
                        gap: "15px",
                      }}
                    >
                      <Field
                        label="Agreement Holder Name"
                        value={
                          parsed.courtDocVerification?.specificSuit
                            ?.agreementHolderName
                        }
                      />
                      <Field
                        label="OS No"
                        value={parsed.courtDocVerification?.specificSuit?.osNo}
                      />
                      <Field
                        label="EP No"
                        value={parsed.courtDocVerification?.specificSuit?.epNo}
                      />
                    </div>
                  </SubSection>
                )}

                {/* Appeal - Only show if its show status is true */}
                {shouldShowSubSection(
                  parsed.courtDocVerification,
                  "isshowAppealStatus",
                ) && (
                  <SubSection title="Appeal">
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fill, minmax(250px, 1fr))",
                        gap: "15px",
                      }}
                    >
                      <Field
                        label="Lower Court Name"
                        value={
                          parsed.courtDocVerification?.appeal?.lowerCourtName
                        }
                      />
                      <Field
                        label="Lower Court Case No"
                        value={
                          parsed.courtDocVerification?.appeal?.lowerCourtcaseNo
                        }
                      />
                      <Field
                        label="Appeal Court Name"
                        value={
                          parsed.courtDocVerification?.appeal?.appealCourtName
                        }
                      />
                      <Field
                        label="Appeal Case No"
                        value={
                          parsed.courtDocVerification?.appeal?.appealCaseNo
                        }
                      />
                      <Field
                        label="Is Lower Court"
                        value={
                          parsed.courtDocVerification?.appeal?.isLowerCourt
                        }
                      />
                      <Field
                        label="Lower Court  Judjment reversed"
                        value={
                          parsed.courtDocVerification?.appeal?.lowerCourtReverse
                        }
                      />
                      <Field
                        label="Lower Court  Judjement Confirm"
                        value={parsed.courtDocVerification?.appeal?.alter}
                      />
                    </div>
                  </SubSection>
                )}

                {/* Court Auction Purchase - Only show if its show status is true */}
                {shouldShowSubSection(
                  parsed.courtDocVerification,
                  "isshowCourtAuctionPurchaseStatus",
                ) && (
                  <SubSection title="Court Auction Purchase">
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fill, minmax(250px, 1fr))",
                        gap: "15px",
                      }}
                    >
                      <Field
                        label="OS No"
                        value={
                          parsed.courtDocVerification?.courtAuctionPurchase
                            ?.osNo
                        }
                      />
                      <Field
                        label="EP No"
                        value={
                          parsed.courtDocVerification?.courtAuctionPurchase
                            ?.epNo
                        }
                      />
                      <Field
                        label="Bidder Name"
                        value={
                          parsed.courtDocVerification?.courtAuctionPurchase
                            ?.bidderName
                        }
                      />
                      <Field
                        label="Property Holder Name"
                        value={
                          parsed.courtDocVerification?.courtAuctionPurchase
                            ?.propertyHolderName
                        }
                      />
                      <Field
                        label="Bank Mortgage Involved?"
                        value={
                          parsed.courtDocVerification?.courtAuctionPurchase
                            ?.isBankMortageInvolved
                        }
                      />
                      <Field
                        label="Mother Available?"
                        value={
                          parsed.courtDocVerification?.courtAuctionPurchase
                            ?.isMotherAvailable
                        }
                      />
                      <Field
                        label="Sales Available?"
                        value={
                          parsed.courtDocVerification?.courtAuctionPurchase
                            ?.isSalesAvailable
                        }
                      />
                    </div>
                  </SubSection>
                )}

                {/* SARFESI Proceeding - Only show if its show status is true */}
                {shouldShowSubSection(
                  parsed.courtDocVerification,
                  "isshowSarfesiProceedingStatus",
                ) && (
                  <SubSection title="SARFESI Proceeding">
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fill, minmax(250px, 1fr))",
                        gap: "15px",
                      }}
                    >
                      <Field
                        label="Possession  Notice Date(Period 60 day Expired)"
                        value={
                          parsed.courtDocVerification?.sarfesiProceeding
                            ?.possessionDate
                        }
                      />
                      <Field
                        label="Symbolic Possession Date (Period 60 day Expired)"
                        value={
                          parsed.courtDocVerification?.sarfesiProceeding
                            ?.symbolicPossession
                        }
                      />
                      <Field
                        label="Borrower Notice Date"
                        value={
                          parsed.courtDocVerification?.sarfesiProceeding
                            ?.borrowerNotice
                        }
                      />
                      <Field
                        label="Auction Date"
                        value={
                          parsed.courtDocVerification?.sarfesiProceeding
                            ?.auctionDate
                        }
                      />
                      <Field
                        label="Paid 15 Percent?"
                        value={
                          parsed.courtDocVerification?.sarfesiProceeding
                            ?.isPaid15Percent
                        }
                      />
                      <Field
                        label="Sales Certificate Obtained"
                        value={
                          parsed.courtDocVerification?.sarfesiProceeding
                            ?.isSalesCertificate
                        }
                      />
                      <Field
                        label="Registered"
                        value={
                          parsed.courtDocVerification?.sarfesiProceeding
                            ?.isRegistered
                        }
                      />
                    </div>
                  </SubSection>
                )}
              </div>
            )}
          </div>
        )}

        {/* Original Document Verification */}
        {isSectionMeaningful("originalDocVerification") && (
          <div style={{ marginBottom: "20px" }}>
            <SectionHeader
              title="Original Document Verification"
              sectionKey="originalDocVerification"
              bgColor={parsed.courtDocVerification?.bgColor}
            />
            {isOpen("originalDocVerification") && (
              <div style={sectionStyle(parsed.courtDocVerification?.bgColor)}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(250px, 1fr))",
                    gap: "15px",
                  }}
                >
                  <Field
                    label="Title Deed Original availability"
                    value={parsed.originalDocVerification?.isIAAvailable}
                  />
                  <Field
                    label="(N-1) Immedite parent document for title deed original availabilty"
                    value={
                      parsed.originalDocVerification
                        ?.immediateParentDocAvailable
                    }
                  />
                  <Field
                    label="Last 13 Years document original availabilty"
                    value={
                      parsed.originalDocVerification?.last13YearsDocAvailable
                    }
                  />
                  <Field
                    label="Same Extent document original availability"
                    value={
                      parsed.originalDocVerification?.sameExtentDocAvailable
                    }
                  />
                  <Field
                    label="Larger Extent documents wrongly mentioned original in LOD"
                    value={
                      parsed.originalDocVerification
                        ?.largerExtentDocWronglyMentioned
                    }
                  />
                  <Field
                    label="Pre Disbursement  Verified"
                    value={
                      parsed.originalDocVerification?.preDisbursementVerified
                    }
                  />
                  <Field
                    label="Post Disbursement Verified"
                    value={
                      parsed.originalDocVerification?.postDisbursementVerified
                    }
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Remarks */}
        {parsed?.remarks && (
          <div
            style={{
              marginTop: "30px",
              padding: "20px",
              backgroundColor: "#f8d7da",
              borderRadius: "8px",
              border: "1px solid #f5c6cb",
            }}
          >
            <strong style={{ color: "#721c24" }}>Remarks:</strong>
            <p
              style={{ margin: "10px 0 0", color: "#721c24", fontSize: "15px" }}
            >
              {parsed.remarks}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PreviewChecklist;